import { useState, useCallback } from "react";

interface ConfirmationOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: "primary" | "success" | "warning" | "danger";
  icon?: string;
}

interface UseConfirmationDialogReturn {
  isOpen: boolean;
  showConfirmation: (options: ConfirmationOptions) => Promise<boolean>;
  hideConfirmation: () => void;
  confirmationProps: ConfirmationOptions & {
    onConfirm: () => void;
    onClose: () => void;
  };
}

export const useConfirmationDialog = (): UseConfirmationDialogReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmationOptions>({
    title: "",
    message: "",
  });
  const [resolvePromise, setResolvePromise] = useState<((value: boolean) => void) | null>(null);

  const showConfirmation = useCallback((opts: ConfirmationOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setOptions(opts);
      setResolvePromise(() => resolve);
      setIsOpen(true);
    });
  }, []);

  const hideConfirmation = useCallback(() => {
    setIsOpen(false);
    if (resolvePromise) {
      resolvePromise(false);
      setResolvePromise(null);
    }
  }, [resolvePromise]);

  const handleConfirm = useCallback(() => {
    if (resolvePromise) {
      resolvePromise(true);
      setResolvePromise(null);
    }
    setIsOpen(false);
  }, [resolvePromise]);

  return {
    isOpen,
    showConfirmation,
    hideConfirmation,
    confirmationProps: {
      ...options,
      onConfirm: handleConfirm,
      onClose: hideConfirmation,
    },
  };
};
